import { useEffect, useState, useCallback, forwardRef } from "react";
import Basic from "../Basic";
import DonutChart from "../../UI/Charts/DonutChart";
import useApi from "../../../Hooks/useApi";
import LoadingSpinner from "../../Shared/LoadingSpinner";
import { NoDataBox } from "../../UI/Charts/NoDataFoundMessage";
import SdgIndicator from "../../SdgIndicator";

export const getAcronym = (indicator) => {

  const map = {
    "SW Generation": "SW",
    SWTP: "SWTP",
    "FS Generation": "FS",
    "FS Collection": "FS",
    FSTP: "FSTP",
    "Safely managed": "Safely managed",
    Basic: "Basic",
    Limited: "Limited",
    Unimproved: "Unimproved",
    "Open defecation": "Open Defecation",
  };
  return map[indicator] || null;
};

export const SanitationSWMData = ({ title, data = [], isComparison }) => {
  const color =
    title === "SWM Summary"
      ? "#2F4B7C"
      : title === "Sanitation Profile"
        ? "#FFA600"
        : "";

  const layoutClass =
    isComparison || !data || data.length === 0
      ? "space-y-5 "
      : "space-y-2 2xl:space-y-5 2xl:flex items-center gap-5 ";

  const hasData = data && data.length > 0;

  return (
    <div className={layoutClass}>
      <h1
        className={`text-[#2F4B7C] ${!hasData ? "text-start" : "text-center md:text-start"
          } text-lg 2xl:text-xl my-auto font-medium ${!isComparison ? "w-full 2xl:w-1/4" : ""}`}
      >
        {title}
      </h1>
      <div
        className={`flex flex-wrap gap-5 ${hasData ? "justify-center md:justify-start" : "justify-start"}`}
      >
        {data && data.length > 0 ? (
          data.map((item, index) => (
            <Basic
              key={index}
              data={{
                ...item,
                text: item.indicator,
                color,
                acronymKey: getAcronym(item.indicator),
              }}
            />
          ))
        ) : (
          <NoDataBox />
        )}
      </div>
    </div>
  );
};

const isFalsy = (value) =>
  value === null || value === undefined || value === "";

export const checkFalsyValues = (dataObject) => {
  const result = {};
  for (const [category, indicators] of Object.entries(dataObject)) {
    result[category] = indicators.map((item) => ({
      ...item,
      value: isFalsy(item.value) ? "N/F" : item.value,
    }));
  }
  return result;
};

export const BoxTitle = forwardRef(
  ({ title, id, cityName, style = {} }, ref) => (
    <h1
      className="text-start text-[#2F4B7C] text-2xl font-semibold mb-8"
      style={style}
      id={id}
      ref={ref}
    >
      {title} <span className="ml-1 font-semibold">{cityName}</span>
    </h1>
  ),
);
const Overview = ({ isComparison, firstCity, secondCity }) => {
  const { request, loading } = useApi({ isPublic: true });
  const [firstCityOverview, setFirstCityOverview] = useState();
  const [secondCityOverview, setSecondCityOverview] = useState();


  const getOverviewData = useCallback(async (city, year, setter) => {
    try {
      const res = await request(
        "GET",
        `/v1/city-profile/overview?location=${city}&year=${year}`,
        {},
        "survey_service",
      );
      setter(checkFalsyValues(res));
    } catch (err) {
      console.error(err);
    }
  }, [request]);

  useEffect(() => {
    if (firstCity?.city?.id && firstCity?.year && firstCity.dataSource) {
      getOverviewData(firstCity.city.id, firstCity.year, setFirstCityOverview);
    } else {
      setFirstCityOverview();
    }
  }, [firstCity, getOverviewData]);

  useEffect(() => {
    if (
      isComparison &&
      secondCity?.city?.id &&
      secondCity?.year &&
      secondCity.dataSource
    ) {
      getOverviewData(
        secondCity.city.id,
        secondCity.year,
        setSecondCityOverview,
      );
    } else {
      setSecondCityOverview();
    }
  }, [secondCity, isComparison, getOverviewData]);

  if (loading) return <LoadingSpinner message="Loading Overview..." />;

  const getSectionData = (overview, key) => overview?.[key] || [];

  const layoutClass = isComparison
    ? ""
    : `flex flex-col ${getSectionData(firstCityOverview, "SWM Summary").length > 0 ||
      getSectionData(firstCityOverview, "Sanitation Profile").length > 0
      ? "xl:flex-row xl:items-center mt-5"
      : ""
    }`;

  const city1SDGData = firstCityOverview?.["SDG Indicators for Sanitation"] || [];
  const city2SDGData = secondCityOverview?.["SDG Indicators for Sanitation"] || [];

  if (!isComparison && (!firstCityOverview || Object.keys(firstCityOverview).length === 0)) return null;

  return (
    <div id="Overview">
      <BoxTitle title="Overview" style={{ marginBottom: 14, marginTop: 30 }} />

      <div
        className={`grid ${isComparison ? "grid-cols-2 gap-10" : "grid-cols-1 gap-5"} w-full`}
      >
        {(isComparison || city1SDGData.length > 0) && (
          <SdgIndicator data={city1SDGData} isComparison={isComparison} />
        )}

        {isComparison && (
          <SdgIndicator data={city2SDGData} isComparison={isComparison} />
        )}
      </div>

      {/* Main Content Area */}
      <div className={layoutClass}>
        {/* General Information Section */}
        <div
          className={`${isComparison ? "w-full grid sm:grid-cols-2 gap-10 h-auto" : ""} my-5`}
        >
          {[firstCityOverview, secondCityOverview].map((city, idx) => {

            const generalInfoData = getSectionData(city, "General Information");
            if (!isComparison && (!generalInfoData || generalInfoData.length === 0)) return null;

            return (
              <div
                key={idx}
                className={`flex justify-center w-full ${isComparison ? "mb-5 sm:mb-0" : ""}`}
              >
                {generalInfoData && generalInfoData.length > 0 ? (
                  <DonutChart
                    data={generalInfoData?.map((el) => ({
                      ...el,
                      color:
                        el.indicator === "Area"
                          ? "#3ACEF3"
                          : el.indicator === "Wards"
                            ? "#ED5C6B"
                            : el.indicator === "Population"
                              ? "#FE4905"
                              : "#2F4B7B",
                    }))}
                    width={700}
                    height={400}
                    cityName={
                      idx === 1 ? secondCity?.city?.name : firstCity?.city?.name
                    }
                  />
                ) : (
                  <div className="flex flex-col  w-full h-full">
                    <h1 className="text-[#2F4B7C] text-start text-lg 2xl:text-xl font-medium mb-4">
                      General Information
                    </h1>

                    <NoDataBox />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Sanitation Profile & SWM Summary */}
        <div className="space-y-10 flex-grow">
          {["Sanitation Profile", "SWM Summary"].map((title) => (
            <div
              key={title}
              className={`${isComparison ? "grid grid-cols-2 gap-10" : ""} w-full`}
            >
              {[firstCityOverview, secondCityOverview].map((city, idx) => {
                if (!isComparison && idx === 1) return null;

                const data = getSectionData(city, title);


                if (!isComparison && (!data || data.length === 0)) return null;

                return (
                  <SanitationSWMData
                    key={idx}
                    title={title}
                    data={data}
                    isComparison={isComparison}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Overview;
